import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

/**
 * ProtectedRoute — guards a route based on authentication and optional role.
 *
 * Behaviour:
 *  - while loading: shows a centred gold spinner (no redirect)
 *  - not logged in: redirect to /login (preserves intended destination via state)
 *  - wrong role: redirect to /dashboard
 *  - logged in & role matches (or no role required): render children
 *
 * @param {React.ReactNode} children
 * @param {"admin"|"expert"|"user"|undefined} role  required role (omit for any logged-in user)
 */
export default function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  const loc = useLocation();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="protected-loading">
        <div className="h-10 w-10 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  }

  if (role && user.role !== role) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
